import { Button, Icon, IconButton, makeStyles, Paper, Theme } from '@material-ui/core';
import { capitalizeAll, capitalizeFirst, EMAIL_VALIDATION_REGEX, ModeSelectInputField, ModeTextInputField, SelectInputOption, useModeFormStyle }
    from '@moderepo/mode-app-components';
import clsx from 'clsx';
import produce, { Draft } from 'immer';
import { useCallback, useMemo, useState } from 'react';
import { AlertRecipientType, AlertAction, AlertRecipientEmail, AlertRecipient, AlertRecipientEvent } from '@moderepo/mode-apis';
import { AlertRuleEditorStep, ALERT_RULE_MAX_ACTIONS, ALERT_RULE_MAX_ACTION_RECIPIENTS, useEditAlertRuleStyle } from '.';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectProject } from '../../../utils/dataStateHooks';
import { getAlertRecipientTypeDisplayName } from '..';


const useStyle = makeStyles((theme: Theme) => {
    return {
        actionSetupForm: {
            display      : 'flex',
            flexDirection: 'column',
            position     : 'relative',
        },

        recipientInputsContainer: {
            display      : 'flex',
            flexDirection: 'column',
            width        : '100%',
            marginBottom : theme.spacing(2),
        },

        recipientTypeInputField: {
            flex: 1,
        },

        recipientEmailInputField: {
            flex: 1,
        },

        addRecipientButton: {
            alignSelf: 'center',
            width    : 'fit-content',
        },

        removeRecipientButton: {
            height   : 'fit-content',
            alignSelf: 'flex-start',
            marginTop: 20,
        },

        addEntityButton: {
            marginTop: theme.spacing(2),
            display  : 'inline-block',
            height   : 'fit-content',
        },

        removeActionButton: {
            position : 'absolute',
            top      : 0,
            right    : 0,
            transform: 'translate3d(50%, -50%, 0)',
        },

        emptyActionError: {
            color    : theme.palette.text.primary,
            width    : '100%',
            flex     : 1,
            textAlign: 'center',
            margin   : theme.spacing(2, 0),
            fontSize : 'larger',
        },


        emptyRecipientError: {
            color    : theme.palette.error.main,
            width    : '100%',
            flex     : 1,
            textAlign: 'center',
            margin   : theme.spacing(2, 0),
            fontSize : 'larger',
        },

        [theme.breakpoints.up('sm')]: {
            recipientInputsContainer: {
                flexDirection: 'row',
            },

            recipientInputField: {
                '&:last-child:not(:first-child)': {
                },
                '&:not(:last-child)': {
                    marginRight: theme.spacing(1),
                },
            },

            recipientTypeInputField: {
                minWidth: '10em',
                flex    : 0,
            },

            recipientEmailInputField: {
                flex    : 1,
                overflow: 'hidden',
            },
        },
    };
});


// The regular expression to test the Event Type with
const EVENT_TYPE_FORMAT = /^[a-zA-Z](?:[-_]?[a-zA-Z0-9]+)*$/;


/**
 * The structure for the Notification Step errors. This interface contains errors for ONE action
 */
interface StepNotificationsInputErrors {
    filter?: string | undefined;
    recipients?: ({
        recipientType?: string | undefined;
        email?: string | undefined;
        eventType?: string | undefined;
    } | undefined)[] | undefined;
    noRecipients?: string | undefined;
}


/**
 * TODO - Update the interface for the filter once we support filter
 */
interface ActionFilterInputs {
}


/**
 * The input interface for 1 recipient. For now, we only support 1 type of recipient, EMAIL, which require an "email" input. If we support
 * other type of recipient later e.g. SLACK, we might need other fields e.g. "channel"
 */
interface ActionRecipientInputs {
    readonly recipientType?: AlertRecipientType | undefined;
    readonly email?: string | undefined;
    readonly eventType?: string | undefined;
}


/**
 * The input interface that contains the inputs for ONE action. Each action has 1 filter and an array of recipients
 */
interface ActionInputs {
    readonly filter?: ActionFilterInputs | undefined;
    readonly recipients?: readonly ActionRecipientInputs[] | undefined;
}



/**
 * Convert actions from an existing alert rule to ActionsInputs
 */
const convertAlertRuleActionsToInputs = (
    existingAlertRuleActions: readonly AlertAction[] | undefined,
): readonly ActionInputs[] => {

    if (existingAlertRuleActions) {
        return existingAlertRuleActions.map((action) => {
            return {
                filters   : action.filter,
                recipients: action.recipients,
            };
        });
    }

    return [];
};


/**
 * Given the inputs for ONE Recipient, check if the inputs object contains values for the required fields and check if the values are
 * valid. Return "undefined" if the inputs are valid. Return an error object if the inputs contains error.
 */
const validateRecipientInputs = (inputs: ActionRecipientInputs): {
    recipientType?: string | undefined;
    email?: string | undefined;
} | undefined => {

    const inputErrors: {
        recipientType?: string | undefined;
        email?: string | undefined;
        eventType?: string| undefined;
    } = {};

    if (!inputs.recipientType) {
        inputErrors.recipientType = 'error.input.generic.empty';
    } else if (inputs.recipientType === AlertRecipientType.EMAIL) {
        // If recipientType is EMAIL then need to make sure the user provided an email and the email is valid
        if (!inputs.email) {
            inputErrors.email = 'error.input.generic.empty';
        } else if (!EMAIL_VALIDATION_REGEX.test(inputs.email)) {
            inputErrors.email = 'error.input.generic.invalid';
        }
    } else if (inputs.recipientType === AlertRecipientType.EVENT) {
        // If recipientType is EVENT then need to make sure the user provided a valid string
        if (!inputs.eventType) {
            inputErrors.eventType = 'error.input.generic.empty';
        } else if (!EVENT_TYPE_FORMAT.test(inputs.eventType)) {
            inputErrors.eventType = 'error.input.generic.invalid';
        }
    }

    const hasErrors = Object.values(inputErrors).some((error) => {
        return error !== undefined;
    });

    if (hasErrors) {
        return inputErrors;
    }

    return undefined;
};



/**
 * The required props for the EditAlertRuleStepInfo in addition to the props inherited from AlertRuleEditorStep
 */
export interface EditAlertRuleStepNotificationsProps extends AlertRuleEditorStep {
    readonly alertRuleActions?: readonly AlertAction[] | undefined;
    readonly onStepCompleted: (actions: readonly AlertAction[])=> void;
}


/**
 * This is the Edit Alert Rule Info step which is the step which the user needs to provide the basic info for the alert rule e.g.
 * name, description, etc...
 */
export const EditAlertRuleStepNotifications: React.FC<EditAlertRuleStepNotificationsProps> = (props: EditAlertRuleStepNotificationsProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const sharedClasses = useEditAlertRuleStyle();
    const classes = useStyle();
    const formClasses = useModeFormStyle();

    // The state to keep track of the inputs for all the actions
    const [actionsInputs, setActionsInputs] = useState<readonly ActionInputs[]>(
        convertAlertRuleActionsToInputs(props.alertRuleActions),
    );

    // The state to keep track of the input errors for all the action inputs
    const [inputErrors, setInputErrors] = useState<readonly(StepNotificationsInputErrors | undefined)[]>([]);



    /**
     * Create and cache the list of recipient type options to be passed to the SELECT recipient type input
     */
    const recipientTypeOptions = useMemo((): readonly SelectInputOption<AlertRecipientType>[] => {
        return Object.values(AlertRecipientType).map((type) => {
            return {
                label: capitalizeAll(trans(getAlertRecipientTypeDisplayName(type))),
                value: type,
            };
        });
    }, [trans]);



    /**
     * On Add action button click handler. We will add an empty ActionInputs object to the array of actionsInputs
     */
    const addAction = useCallback(() => {
        props.onStepError(false);

        setActionsInputs((currentActions) => {
            return [
                ...currentActions,
                {
                    filter    : undefined,
                    // Since we only have EMAIL recipient type, we can automatically create 1 recipient and initialize it with EMAIL type.
                    // When we have other types of recipient then we can remove this
                    recipients: [{
                        recipientType: AlertRecipientType.EMAIL,
                    }],
                },
            ];
        });
    }, [props]);


    /**
     * On remove action button click handler. We will remove the action at "actionIndex" and also the input errors at "actionIndex"
     */
    const removeAction = useCallback((actionIndex: number) => {
        props.onStepError(false);

        if (actionsInputs.length > actionIndex) {
            setActionsInputs((currentActions) => {
                return currentActions.filter((action, i) => {
                    return i !== actionIndex;
                });
            });
        }

        if (inputErrors.length > actionIndex) {
            setInputErrors((currentInputErrors) => {
                return currentInputErrors.filter((inputErrors, i) => {
                    return i !== actionIndex;
                });
            });
        }
    }, [props, actionsInputs.length, inputErrors.length]);


    /**
     * Add a Recipient inputs to the action at "actionIndex"
     */
    const addRecipient = useCallback((actionIndex) => {
        props.onStepError(false);

        setActionsInputs((currentActions) => {
            // Make a copy of the currentActions
            const newActions = [...currentActions];

            // Add a new recipient inputs to the recipients array of the action at "actionIndex"
            const targetAction = newActions[actionIndex];
            const updatedTargetActionRecipients: ActionRecipientInputs[] = [
                ...(targetAction.recipients ?? []),
                {
                    recipientType: AlertRecipientType.EMAIL,
                },
            ];

            // Replace the action at "actionIndex" with the new action
            newActions[actionIndex] = {
                ...targetAction,
                recipients: updatedTargetActionRecipients,
            };

            return newActions;
        });
    }, [props]);


    /**
     * Remove a recipient inputs from the action at "actionIndex" and also remove the errors for the recipient
     */
    const removeRecipient = useCallback((actionIndex: number, recipientIndex: number) => {
        props.onStepError(false);

        setActionsInputs((currentActions) => {
            // Make a copy of the currentActions
            const newActions = [...currentActions];

            // Remove the recipient inputs to the recipients array of the action at "actionIndex"
            const targetAction = newActions[actionIndex];
            const updatedTargetActionRecipients = targetAction.recipients ? targetAction.recipients.filter((recipient, index) => {
                return index !== recipientIndex;
            }) : [];

            // Replace the action at "actionIndex" with the modified action
            newActions[actionIndex] = {
                ...targetAction,
                recipients: updatedTargetActionRecipients,
            };

            return newActions;
        });


        // If we removed recipient inputs at "recipientIndex" then we also need to delete the error for the recipient at that index
        if (inputErrors.length > actionIndex) {
            setInputErrors((currentInputErrors) => {
                const newInputErrors = [...currentInputErrors];
                const targetActionErrors = newInputErrors[actionIndex];

                const updatedRecipientsErrors = targetActionErrors && targetActionErrors.recipients
                    ? targetActionErrors.recipients.filter((recipientError, index) => {
                        return index !== recipientIndex;
                    }) : [];

                newInputErrors[actionIndex] = {
                    ...targetActionErrors,
                    recipients: updatedRecipientsErrors,
                };

                return newInputErrors;
            });
        }
    }, [inputErrors.length, props]);



    /**
     * Submit the user inputs for this step. Before submitting the inputs, we will validate the inputs. If there are no errors,
     * we will notify the parent container by calling ths 'onStepCompleted' callback and provide the parent container with the new
     * inputs and return 'true' for success. If there are errors, we will notify the parent container that there are errors but only
     * if 'dispatchOnError' is true and also return false for failure.
     */
    const submitInputs = useCallback((dispatchOnError: boolean = true): boolean => {

        const newInputErrors: StepNotificationsInputErrors[] = [];


        actionsInputs.forEach((oneActionInputs, actionIndex) => {

            // Create an empty object to store input errors for the action at actionIndex
            newInputErrors[actionIndex] = {};
            const oneActionInputErrors = newInputErrors[actionIndex];


            if (!oneActionInputs.recipients || oneActionInputs.recipients.length === 0) {
                oneActionInputErrors.noRecipients = 'error.input.generic.empty';
            } else {

                // Create an Array of recipient inputs error for storing errors for each recipient
                const recipientsErrors: StepNotificationsInputErrors['recipients'] = [];

                // Make sure ALL the recipients are configured correctly
                oneActionInputs.recipients.forEach((recipient, recipientIndex) => {
                    // Call "validateRecipientInputs" to validate the inputs for 1 recipient and then add the error
                    // object to the recipientsErrors array
                    recipientsErrors[recipientIndex] = validateRecipientInputs(recipient);
                });


                // Now add the recipientsError to the oneActionInputErrors BUT only if there is at least 1 recipient inputs contains errors
                const hasRecipientErrors = recipientsErrors.some((errors) => {
                    return errors !== undefined;
                });
                if (hasRecipientErrors) {
                    oneActionInputErrors.recipients = recipientsErrors;
                }
            }
        });


        // Update the local inputErrors
        setInputErrors(newInputErrors);


        // If there is AT LEAST 1 field in the newInputError, this Step is considered to contain errors.
        const hasErrors = newInputErrors.some((actionsInputErrors) => {
            return Object.values(actionsInputErrors).some((error) => {
                return error !== undefined;
            });
        });


        // If 'dispatchOnError' is specified, dispatch the error or null to the container
        if (dispatchOnError) {
            props.onStepError(hasErrors);
        }


        // If there are no errors, notify the parent container this step is completed and pass the parent the input data
        if (!hasErrors) {

            const actions: AlertAction[] = [];

            // Convert actionsInputs to array of AlertAction. AlertAction is the structure we need to send to backend.
            actionsInputs.forEach((oneActionInputs) => {

                const recipients: AlertRecipient[] = [];
                oneActionInputs.recipients?.forEach((recipientInput) => {
                    // Create AlertRecipientEmail object if 'recipientType' is email
                    if (recipientInput.recipientType === AlertRecipientType.EMAIL && recipientInput.email) {
                        recipients.push({
                            recipientType: AlertRecipientType.EMAIL,
                            email        : recipientInput.email,
                        } as AlertRecipientEmail);
                    }

                    if (recipientInput.recipientType === AlertRecipientType.EVENT && recipientInput.eventType) {
                        recipients.push({
                            recipientType: AlertRecipientType.EVENT,
                            eventType    : recipientInput.eventType,
                        } as AlertRecipientEvent);
                    }
                    // TODO - Add support for other recipient types
                });

                if (recipients && recipients.length > 0) {
                    actions.push({
                        filter    : oneActionInputs.filter,
                        recipients: recipients as [AlertRecipient, ...AlertRecipient[]],
                    });
                }
            });

            props.onStepCompleted(actions);
        }

        return !hasErrors;
    }, [props, actionsInputs]);



    /**
     * Create the input fields required for 1 recipient. For each recipient, the user will need to specify the recipientType and then based
     * on the selected type, we will create the other required inputs. For example, if the user selected EMAIL type, we will show the input
     * field for EMAIL. If the user selected SLACK, we will show the input field for CHANNEL, and so on.
     */
    const createRecipientInputs = useCallback((oneRecipient: ActionRecipientInputs, actionIndex: number, recipientIndex: number) => {

        return (
            <div
                key={`${oneRecipient.recipientType}-${actionIndex}-${recipientIndex}`}
                className={classes.recipientInputsContainer}
            >
                <ModeSelectInputField
                    className={clsx(
                        formClasses.formInputField, sharedClasses.formControl, classes.recipientInputField,
                        classes.recipientTypeInputField, 'form-control',
                    )}
                    fieldName="recipientType"
                    label={capitalizeAll(trans('alerting.edit_alert_rule.label.recipient_type'))}
                    required
                    editable
                    isInEditMode
                    hideEditableIcon
                    value={oneRecipient.recipientType ?? ''}
                    options={recipientTypeOptions}
                    error={trans(inputErrors[actionIndex]?.recipients?.[recipientIndex]?.recipientType ?? '')}
                    onInputChange={(fieldName, newValue) => {

                        // Input changed so clear the error for the recipient at "recipientIndex"
                        props.onStepError(false);
                        setInputErrors(produce(inputErrors, (draft: Draft<typeof inputErrors>) => {
                            const recipientErrors = draft[actionIndex]?.recipients?.[recipientIndex];
                            if (recipientErrors) {
                                delete recipientErrors.recipientType;
                                delete recipientErrors.email;
                            }
                        }));

                        // update the state
                        setActionsInputs((currentActions) => {
                            // Make a copy of the current array of actions
                            const newActions = [
                                ...currentActions,
                            ];

                            const targetAction = newActions[actionIndex];
                            const updatedTargetActionRecipient = targetAction.recipients?.[recipientIndex];

                            const newRecipientInputs: ActionRecipientInputs = {
                                ...updatedTargetActionRecipient,
                                recipientType: newValue,

                                // recipientType changed, we will need to unset 'email' because the 'email' input might not be needed for the
                                // new selected recipient type.
                                email: undefined,
                            };

                            // Create a new recipients array from copying the current recipients array and then replace the recipient at
                            // "recipientIndex" with the new recipientInput
                            const newRecipientsArray: ActionRecipientInputs[] = [
                                ...(targetAction.recipients ?? []),
                            ];
                            newRecipientsArray[recipientIndex] = {
                                ...newRecipientInputs,
                            };

                            // update the action at index
                            newActions[actionIndex] = {
                                ...targetAction,
                                recipients: newRecipientsArray,
                            };

                            // return the new actions
                            return newActions;
                        });
                    }}
                />

                {/* Need to wait until recipientType is selected as EMAIL before we can show the 'email' input */}
                {oneRecipient.recipientType === AlertRecipientType.EMAIL && (
                    <ModeTextInputField
                        className={clsx(
                            formClasses.formInputField, sharedClasses.formControl, classes.recipientInputField,
                            classes.recipientEmailInputField, 'form-control',
                        )}
                        label={capitalizeAll(trans('label.email'))}
                        fieldName="email"
                        required
                        editable
                        isInEditMode
                        hideEditableIcon
                        value={oneRecipient.email ?? ''}
                        error={trans(inputErrors[actionIndex]?.recipients?.[recipientIndex]?.email ?? '')}
                        onInputChange={(fieldName, newValue) => {
                            // Input changed so clear the error for the current step and the updated recipient
                            props.onStepError(false);
                            setInputErrors(produce(inputErrors, (draft: Draft<typeof inputErrors>) => {
                                const recipientErrors = draft[actionIndex]?.recipients?.[recipientIndex];
                                if (recipientErrors) {
                                    delete recipientErrors.email;
                                }
                            }));

                            // update the state
                            setActionsInputs((currentActions) => {
                                // Make a copy of the current array of actions
                                const newActions = [
                                    ...currentActions,
                                ];

                                const targetAction = newActions[actionIndex];
                                const updatedTargetActionRecipient = targetAction.recipients?.[recipientIndex];

                                const newRecipientInputs: ActionRecipientInputs = {
                                    ...updatedTargetActionRecipient,
                                    email: newValue,
                                };

                                // Create a new recipients array from copying the current recipients array and then replace the recipient at
                                // "recipientIndex" with the new recipientInput
                                const newRecipientsArray: ActionRecipientInputs[] = [
                                    ...(targetAction.recipients ?? []),
                                ];
                                newRecipientsArray[recipientIndex] = {
                                    ...newRecipientInputs,
                                };

                                // update the action at index
                                newActions[actionIndex] = {
                                    ...targetAction,
                                    recipients: newRecipientsArray,
                                };

                                // return the new actions
                                return newActions;
                            });
                        }}
                    />
                )}

                {/* Need to wait until recipientType is selected as EVENT before we can show the 'event' input */}
                {oneRecipient.recipientType === AlertRecipientType.EVENT && (
                    <ModeTextInputField
                        className={clsx(
                            formClasses.formInputField, sharedClasses.formControl, classes.recipientInputField,
                            classes.recipientEmailInputField, 'form-control',
                        )}
                        label={capitalizeAll(trans('alerting.edit_notification.label.event_type'))}
                        description={trans('alerting.edit_notification.hint.event_type')}
                        fieldName="eventType"
                        required
                        editable
                        isInEditMode
                        hideEditableIcon
                        value={oneRecipient.eventType ?? ''}
                        error={trans(inputErrors[actionIndex]?.recipients?.[recipientIndex]?.eventType ?? '')}
                        onInputChange={(fieldName, newValue) => {
                            // Input changed so clear the error for the current step and the updated recipient
                            props.onStepError(false);
                            setInputErrors(produce(inputErrors, (draft: Draft<typeof inputErrors>) => {
                                const recipientErrors = draft[actionIndex]?.recipients?.[recipientIndex];
                                if (recipientErrors) {
                                    delete recipientErrors.eventType;
                                }
                            }));

                            // update the state
                            setActionsInputs((currentActions) => {
                                // Make a copy of the current array of actions
                                const newActions = [
                                    ...currentActions,
                                ];

                                const targetAction = newActions[actionIndex];
                                const updatedTargetActionRecipient = targetAction.recipients?.[recipientIndex];

                                const newRecipientInputs: ActionRecipientInputs = {
                                    ...updatedTargetActionRecipient,
                                    eventType: newValue,
                                };

                                // Create a new recipients array from copying the current recipients array and then replace the recipient at
                                // "recipientIndex" with the new recipientInput
                                const newRecipientsArray: ActionRecipientInputs[] = [
                                    ...(targetAction.recipients ?? []),
                                ];
                                newRecipientsArray[recipientIndex] = {
                                    ...newRecipientInputs,
                                };

                                // update the action at index
                                newActions[actionIndex] = {
                                    ...targetAction,
                                    recipients: newRecipientsArray,
                                };

                                // return the new actions
                                return newActions;
                            });
                        }}
                    />
                )}

                {/* TODO - Add support for other types of RECIPIENT */}


                {/* Add Remove recipient button */}
                <Button
                    className={classes.removeRecipientButton}
                    color="secondary"
                    size="small"
                    onClick={() => {
                        removeRecipient(actionIndex, recipientIndex);
                    }}
                >
                    {trans('label.remove')}
                </Button>
            </div>
        );
    }, [classes.recipientEmailInputField, classes.recipientInputField, classes.recipientInputsContainer, classes.recipientTypeInputField,
        classes.removeRecipientButton, formClasses.formInputField, inputErrors, props, recipientTypeOptions, removeRecipient,
        sharedClasses.formControl, trans]);



    return (
        <div className={sharedClasses.stepContentBody}>
            <div className={clsx(sharedClasses.stepContentForm)}>
                <div className={clsx(sharedClasses.stepSubtitle)}>
                    {trans('alerting.edit_alert_rule.step_notifications.subtitle')}
                </div>

                {/* Show Message if there are no recipient configured */}
                {actionsInputs.length === 0 && (
                    <div className={classes.emptyActionError}>
                        {capitalizeFirst(trans('alerting.edit_alert_rule.step_notifications.actions.errors.empty'))}
                    </div>
                )}

                {actionsInputs.map((oneActionInputs, actionIndex) => {
                    return (
                        <Paper
                            key={actionIndex.toString()}
                            elevation={1}
                            className={clsx(sharedClasses.formControlGroup, classes.actionSetupForm)}
                        >
                            {/* Add Remove Action button */}
                            <IconButton
                                className={classes.removeActionButton}
                                color="secondary"
                                size="small"
                                onClick={() => {
                                    removeAction(actionIndex);
                                }}
                            >
                                <Icon fontSize="small">cancel</Icon>
                            </IconButton>

                            {oneActionInputs.recipients && oneActionInputs.recipients.length > 0 ? (
                                /* Loop through the array of recipients and create 1 row of recipient inputs per recipient */
                                <>
                                    {oneActionInputs.recipients.map((oneRecipient, recipientIndex) => {
                                        return (
                                            createRecipientInputs(oneRecipient, actionIndex, recipientIndex)
                                        );
                                    })}
                                </>
                            ) : (
                                /* Show Message if there are no recipient configured */
                                <div className={classes.emptyRecipientError}>
                                    {capitalizeFirst(trans('alerting.edit_alert_rule.step_notifications.recipients.errors.empty'))}
                                </div>
                            )}

                            {/* Enable "Add Recipient" button if we haven't reached the limit number of recipients */}
                            <Button
                                className={classes.addRecipientButton}
                                variant="contained"
                                color="default"
                                size="small"
                                disabled={oneActionInputs.recipients && oneActionInputs.recipients.length >= ALERT_RULE_MAX_ACTION_RECIPIENTS}
                                onClick={() => {
                                    addRecipient(actionIndex);
                                }}
                            >
                                {capitalizeAll(trans('label.add_recipient'))}
                            </Button>
                        </Paper>
                    );
                })}
            </div>

            <div className={sharedClasses.stepContentActionsContainer}>
                <div className={sharedClasses.stepContentLeftActionsContainer}>
                    <Button
                        variant="contained"
                        color="default"
                        disabled={actionsInputs.length >= ALERT_RULE_MAX_ACTIONS}
                        onClick={addAction}
                    >
                        {capitalizeAll(trans('label.add_notification'))}
                    </Button>
                </div>
                <div className={sharedClasses.stepContentRightActionsContainer}>
                    <Button
                        disabled={!props.canGoBack}
                        onClick={() => {
                            // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                            // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will
                            // not save the user's input since it is invalid anyway.
                            props.onStepError(false);
                            submitInputs(false);
                            props.onPrevStepClick();
                        }}
                        className={sharedClasses.navButton}
                        startIcon={<Icon>navigate_before</Icon>}
                    >
                        {capitalizeAll(trans('label.back'))}
                    </Button>
                    <Button
                        onClick={() => {
                            if (submitInputs()) {
                                props.onNextStepClick();
                            }
                        }}
                        className={sharedClasses.navButton}
                        endIcon={<Icon>navigate_next</Icon>}
                    >
                        {capitalizeAll(trans(props.isLastStep ? 'label.finish' : 'label.next'))}
                    </Button>
                    {!props.isNewAlertRule && (
                        <Button
                            variant="contained"
                            color="default"
                            size="small"
                            onClick={() => {
                                if (props.onGotoLastStepClick && submitInputs()) {
                                    props.onGotoLastStepClick();
                                }
                            }}
                            className={clsx(sharedClasses.navButton, 'finish')}
                        >
                            {capitalizeAll(trans('label.finish'))}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
